<template>
  <div class="column content-box">
    <woot-modal-header :header-title="modalTitle" />

    <form class="row" @submit.prevent="submit">
      <div class="medium-12 columns">
        <label :class="{ error: $v.localQuestion.label.$error }">
          {{
            $t('OPPORTUNITY_PIPELINES.NEGOTIATION_FORM.QUESTION_MODAL.LABEL')
          }}
          <input
            v-model.trim="localQuestion.label"
            type="text"
            maxlength="255"
            @input="$v.localQuestion.label.$touch"
          />
          <span v-if="$v.localQuestion.label.$error" class="message">
            {{
              $t(
                'OPPORTUNITY_PIPELINES.NEGOTIATION_FORM.QUESTION_MODAL.LABEL_REQUIRED'
              )
            }}
          </span>
        </label>

        <label>
          {{
            $t(
              'OPPORTUNITY_PIPELINES.NEGOTIATION_FORM.QUESTION_MODAL.DESCRIPTION'
            )
          }}
          <input
            v-model.trim="localQuestion.description"
            type="text"
            maxlength="255"
            :placeholder="
              $t(
                'OPPORTUNITY_PIPELINES.NEGOTIATION_FORM.QUESTION_MODAL.DESCRIPTION_PLACEHOLDER'
              )
            "
          />
        </label>

        <div class="field">
          <p class="field-label">
            {{
              $t('OPPORTUNITY_PIPELINES.NEGOTIATION_FORM.QUESTION_MODAL.TYPE')
            }}
          </p>
          <multiselect
            v-model="selectedType"
            :options="typeOptions"
            track-by="id"
            label="name"
            :multiple="false"
            :allow-empty="false"
            :disabled="isEdit"
            :searchable="false"
            :select-label="$t('FORMS.MULTISELECT.ENTER_TO_SELECT')"
            deselect-label=""
          />
          <p v-if="isEdit" class="field-hint">
            {{
              $t(
                'OPPORTUNITY_PIPELINES.NEGOTIATION_FORM.QUESTION_MODAL.TYPE_LOCKED'
              )
            }}
          </p>
          <p v-if="isCheckboxType" class="field-hint">
            {{
              $t(
                'OPPORTUNITY_PIPELINES.NEGOTIATION_FORM.QUESTION_MODAL.CHECKBOX_HINT'
              )
            }}
          </p>
        </div>

        <div v-if="isListType" class="field">
          <p class="field-label" :class="{ error: showListValuesError }">
            {{
              $t(
                'OPPORTUNITY_PIPELINES.NEGOTIATION_FORM.QUESTION_MODAL.LIST_VALUES'
              )
            }}
          </p>
          <multiselect
            v-model="listValues"
            class="list-values-input"
            label="name"
            track-by="name"
            :options="[]"
            :multiple="true"
            :taggable="true"
            :close-on-select="false"
            :tag-placeholder="''"
            :placeholder="
              $t(
                'OPPORTUNITY_PIPELINES.NEGOTIATION_FORM.QUESTION_MODAL.LIST_VALUES_PLACEHOLDER'
              )
            "
            @tag="addListValue"
            @close="listValuesTouched = true"
          />
          <span v-if="showListValuesError" class="message error-message">
            {{
              $t(
                'OPPORTUNITY_PIPELINES.NEGOTIATION_FORM.QUESTION_MODAL.LIST_VALUES_REQUIRED'
              )
            }}
          </span>
        </div>

        <div class="field">
          <p class="field-label">
            {{
              $t(
                'OPPORTUNITY_PIPELINES.NEGOTIATION_FORM.QUESTION_MODAL.REQUIRED_STAGE'
              )
            }}
          </p>
          <multiselect
            v-model="selectedStage"
            :options="stageOptions"
            track-by="id"
            label="name"
            :multiple="false"
            :allow-empty="false"
            :searchable="false"
            :select-label="$t('FORMS.MULTISELECT.ENTER_TO_SELECT')"
            deselect-label=""
          />
          <p class="field-hint">
            {{
              $t(
                'OPPORTUNITY_PIPELINES.NEGOTIATION_FORM.QUESTION_MODAL.REQUIRED_STAGE_HINT'
              )
            }}
          </p>
        </div>
      </div>

      <div class="modal-footer">
        <woot-button
          type="button"
          color-scheme="secondary"
          @click="$emit('close')"
        >
          {{ $t('OPPORTUNITY_PIPELINES.ACTIONS.CANCEL') }}
        </woot-button>
        <woot-button :is-loading="isSaving" :disabled="isInvalid">
          {{ $t('OPPORTUNITY_PIPELINES.ACTIONS.SAVE') }}
        </woot-button>
      </div>
    </form>
  </div>
</template>

<script>
import { required } from 'vuelidate/lib/validators';
import {
  NEGOTIATION_QUESTION_TYPES,
  negotiationQuestionTypeLabelKey,
} from 'dashboard/helper/opportunityNegotiationFormHelper';

const defaultQuestion = () => ({
  label: '',
  description: '',
  question_type: 'text',
  list_values: [],
  required_pipeline_stage_id: null,
});

export default {
  props: {
    question: {
      type: Object,
      default: () => ({}),
    },
    stages: {
      type: Array,
      default: () => [],
    },
    isSaving: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      localQuestion: defaultQuestion(),
      listValues: [],
      listValuesTouched: false,
    };
  },
  validations: {
    localQuestion: {
      label: { required },
    },
  },
  computed: {
    isEdit() {
      return !!this.question.id;
    },
    modalTitle() {
      return this.isEdit
        ? this.$t(
            'OPPORTUNITY_PIPELINES.NEGOTIATION_FORM.QUESTION_MODAL.EDIT_TITLE'
          )
        : this.$t(
            'OPPORTUNITY_PIPELINES.NEGOTIATION_FORM.QUESTION_MODAL.NEW_TITLE'
          );
    },
    isListType() {
      return this.localQuestion.question_type === 'list';
    },
    isCheckboxType() {
      return this.localQuestion.question_type === 'checkbox';
    },
    typeOptions() {
      return NEGOTIATION_QUESTION_TYPES.map(type => ({
        id: type,
        name: this.$t(negotiationQuestionTypeLabelKey(type)),
      }));
    },
    selectedType: {
      get() {
        return this.typeOptions.find(
          option => option.id === this.localQuestion.question_type
        );
      },
      set(option) {
        this.localQuestion.question_type = option.id;
      },
    },
    stageOptions() {
      return [
        {
          id: null,
          name: this.$t(
            'OPPORTUNITY_PIPELINES.NEGOTIATION_FORM.QUESTION_MODAL.NOT_REQUIRED'
          ),
        },
        ...this.stages.map(stage => ({
          id: stage.id,
          name:
            stage.status === 'inactive'
              ? this.$t(
                  'OPPORTUNITY_PIPELINES.NEGOTIATION_FORM.INACTIVE_STAGE',
                  {
                    stageName: stage.name,
                  }
                )
              : stage.name,
        })),
      ];
    },
    selectedStage: {
      get() {
        return this.stageOptions.find(
          option => option.id === this.localQuestion.required_pipeline_stage_id
        );
      },
      set(option) {
        this.localQuestion.required_pipeline_stage_id = option.id;
      },
    },
    isListValuesInvalid() {
      return this.isListType && !this.listValues.length;
    },
    showListValuesError() {
      return this.listValuesTouched && this.isListValuesInvalid;
    },
    isInvalid() {
      return this.$v.localQuestion.label.$invalid || this.isListValuesInvalid;
    },
  },
  watch: {
    question: {
      immediate: true,
      handler(question) {
        this.localQuestion = {
          ...defaultQuestion(),
          ...question,
          description: question.description || '',
          required_pipeline_stage_id:
            question.required_pipeline_stage_id || null,
        };
        this.listValues = (question.list_values || []).map(name => ({ name }));
        this.listValuesTouched = false;
      },
    },
  },
  methods: {
    addListValue(value) {
      const name = value.trim();
      if (!name || this.listValues.some(item => item.name === name)) {
        return;
      }
      this.listValues = [...this.listValues, { name }];
    },
    submit() {
      this.$v.$touch();
      this.listValuesTouched = true;
      if (this.isInvalid) {
        return;
      }

      const payload = {
        label: this.localQuestion.label,
        description: this.localQuestion.description,
        list_values: this.isListType
          ? this.listValues.map(item => item.name)
          : [],
        required_pipeline_stage_id: this.localQuestion
          .required_pipeline_stage_id,
      };
      if (!this.isEdit) {
        payload.question_type = this.localQuestion.question_type;
      }

      this.$emit('submit', payload);
    },
  },
};
</script>

<style lang="scss" scoped>
.field {
  margin-bottom: var(--space-normal);
}

.field-label {
  color: var(--color-body);
  font-size: var(--font-size-small);
  font-weight: var(--font-weight-medium);
  margin-bottom: var(--space-smaller);

  &.error {
    color: var(--r-500);
  }
}

.field-hint {
  color: var(--s-600);
  font-size: var(--font-size-mini);
  margin: var(--space-micro) 0 0;
}

.error-message {
  color: var(--r-500);
  display: block;
  font-size: var(--font-size-mini);
}

.field::v-deep .multiselect {
  margin-bottom: 0;
}

.list-values-input::v-deep .multiselect__content-wrapper {
  display: none;
}

.modal-footer {
  display: flex;
  gap: var(--space-small);
  justify-content: flex-end;
  width: 100%;
}
</style>
